import { useEffect, useMemo, useState } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import ReactGA from 'react-ga4';
import { BrowserRouter, Route, Switch } from 'react-router-dom';

import '../../styles/root.css';
import { trpc } from '../../trpc';
import CADLite from '../../views/CADLite';
import GlobalView from '../../views/Global';
import UKView from '../../views/UK';

type Config = {
  universalDashboardBaseUrl: string;
  universalDashboardApiKey: string;
  googleAnalyticsTrackingID: string;
};

export function App() {
  const queryClient = useMemo(() => new QueryClient(), []);

  const [config, setConfig] = useState<Config | undefined>();

  useEffect(() => {
    async function fetchConfig() {
      const response = await fetch(
        `${process.env.REACT_APP_UNIVERSAL_DASHBOARD_CONFIG_PREFIX}/config/config.json`,
      );
      const data = await response.json();
      setConfig(data);
    }

    fetchConfig();
  }, []);

  useEffect(() => {
    if (config?.googleAnalyticsTrackingID) {
      ReactGA.initialize(config.googleAnalyticsTrackingID);
    }
  }, [config]);

  const trpcClient = useMemo(() => {
    const { universalDashboardBaseUrl, universalDashboardApiKey } =
      config ?? {};

    return trpc.createClient({
      links: [
        httpBatchLink({
          url: universalDashboardBaseUrl ?? '',
          headers() {
            return {
              'x-api-key': universalDashboardApiKey ?? '',
            };
          },
        }),
      ],
    });
  }, [config]);

  if (!config) {
    return null;
  }

  const props = {
    isLoadingConfig: config === undefined,
  };

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Switch>
            <Route path="/global">
              <GlobalView {...props} />
            </Route>
            <Route path="/cad-lite">
              <CADLite />
            </Route>
            <Route path="/">
              <UKView {...props} />
            </Route>
          </Switch>
        </BrowserRouter>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
